import { useEffect, useId, useRef, type KeyboardEvent } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { WRITE_OFF_TABS, type WriteOffTab } from '../model/write-off-tabs.js';

/** Tab paneli shu `id` bilan bog'lanadi (`aria-controls`). */
export const writeOffPanelId = (baseId: string): string => `${baseId}-panel`;
export const writeOffTabId = (baseId: string, tab: WriteOffTab): string => `${baseId}-tab-${tab}`;

const LABEL: Record<WriteOffTab, string> = {
  PENDING: 'writeOff.status.PENDING',
  APPROVED: 'writeOff.status.APPROVED',
  REJECTED: 'writeOff.status.REJECTED',
  ALL: 'writeOff.filterAll',
};

/** Faol tabdagi hisoblagich rangi — holat rangi bilan bir xil ma'noda. */
const ACTIVE_COUNT: Record<WriteOffTab, string> = {
  PENDING: 'bg-warn text-warn-contrast',
  APPROVED: 'bg-success text-success-contrast',
  REJECTED: 'bg-danger text-danger-contrast',
  ALL: 'bg-primary text-primary-contrast',
};

export interface WriteOffTabsProps {
  /** Panel va tablarni bog'laydigan asos — sahifa `useId` dan beradi. */
  baseId: string;
  value: WriteOffTab;
  counts: Record<WriteOffTab, number | undefined>;
  onChange: (tab: WriteOffTab) => void;
  className?: string;
}

/**
 * NAVBAT TABLARI — segment boshqaruvi ko'rinishida, lekin semantikasi TAB.
 *
 * Umumiy `Segmented` ishlatilmadi: u ataylab `role="group"` (navigatsiya),
 * bu yerda esa bitta ro'yxatning ko'rinishlari almashadi va ekran o'quvchi
 * «4 tadan 2-tab» deb eshitishi kerak. Klaviatura WAI-ARIA naqshidagidek:
 * ←/→ qo'shni tabga, Home/End chetiga; fokus faqat faol tabda turadi
 * (roving tabindex), ya'ni `Tab` tugmasi butun guruhni bitta qadamda o'tadi.
 *
 * Tor ekranda panel gorizontal suriladi (scrollbar yashirin) va faol tab
 * o'zi ko'rinishga keladi; kengida panel kontentga qarab, cho'zilmaydi.
 */
export const WriteOffTabs = ({ baseId, value, counts, onChange, className }: WriteOffTabsProps) => {
  const reduceMotion = useReducedMotion() === true;
  const pillId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const refs = useRef(new Map<WriteOffTab, HTMLButtonElement>());

  /* Faol tab ko'rinishga keladi — telefonda «Hammasi» chetda yashirinib
     qolmasin. Faqat gorizontal: sahifa vertikal sakramasin. */
  useEffect(() => {
    const list = listRef.current;
    const node = refs.current.get(value);
    if (list === null || node === undefined) return;
    const left = node.offsetLeft - list.offsetLeft;
    const right = left + node.offsetWidth;
    if (left < list.scrollLeft || right > list.scrollLeft + list.clientWidth) {
      list.scrollTo({
        left: left - (list.clientWidth - node.offsetWidth) / 2,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }
  }, [value, reduceMotion]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const index = WRITE_OFF_TABS.indexOf(value);
    const last = WRITE_OFF_TABS.length - 1;
    const next =
      event.key === 'ArrowRight'
        ? index === last ? 0 : index + 1
        : event.key === 'ArrowLeft'
          ? index === 0 ? last : index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    const tab = WRITE_OFF_TABS[next] as WriteOffTab;
    onChange(tab);
    refs.current.get(tab)?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={t('writeOff.tabsLabel')}
      onKeyDown={onKeyDown}
      className={cn(
        // Fokus halqasi kesilmasin: ichki bo'shliq + `scroll-px`.
        'relative flex max-w-full gap-1 overflow-x-auto overscroll-x-contain p-1',
        'rounded-2xl border border-border bg-surface-2/70 shadow-[inset_0_1px_2px_rgb(10_30_50/0.04)]',
        '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        'w-full sm:w-fit',
        className,
      )}
    >
      {WRITE_OFF_TABS.map((tab) => {
        const active = tab === value;
        const count = counts[tab];
        return (
          <button
            key={tab}
            ref={(node) => {
              if (node === null) refs.current.delete(tab);
              else refs.current.set(tab, node);
            }}
            id={writeOffTabId(baseId, tab)}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={writeOffPanelId(baseId)}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(tab)}
            className={cn(
              'relative isolate flex min-h-[var(--size-control)] shrink-0 grow items-center',
              'justify-center gap-2 rounded-xl px-3.5 text-sm font-medium whitespace-nowrap',
              'transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
              'outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
              'focus-visible:ring-offset-surface-2 sm:grow-0',
              active ? 'text-text' : 'text-text-muted hover:bg-hover hover:text-text',
            )}
          >
            {active ? (
              <motion.span
                layoutId={pillId}
                aria-hidden
                className="absolute inset-0 -z-10 rounded-xl border border-border bg-surface shadow-[var(--shadow-2)]"
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : { type: 'spring', stiffness: 520, damping: 40, mass: 0.9 }
                }
              />
            ) : null}
            <span>{t(LABEL[tab])}</span>
            {count === undefined ? null : (
              <span
                className={cn(
                  'tabular inline-flex min-w-[1.375rem] items-center justify-center rounded-full',
                  'px-1.5 py-px text-[0.6875rem] leading-4 font-semibold',
                  'transition-colors duration-[var(--motion-fast)]',
                  active ? ACTIVE_COUNT[tab] : 'bg-surface text-text-muted ring-1 ring-border',
                )}
              >
                {count}
                <span className="sr-only"> {t('writeOff.countUnit')}</span>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
